/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, delay, put, select } from 'redux-saga/effects'
import { createSagas } from 'src/helpers/redux'
import { IResponse } from 'src/services/auth/http'

import * as usersAuthService from 'src/services/auth/users'
import * as partnerAuthService from 'src/services/auth/partner'
import * as sessionService from 'src/services/session'
import * as accountsService from 'src/services/accounts'
import * as localStorageService from 'src/services/localStorage'
import * as oauthService from 'src/services/auth/oauth'

import { setters } from './index'
import * as state from './state'
import { actions, Actions } from './actions'
import { selectors } from './selectors'
import { IPartner } from 'src/services/auth/partner/type'

import * as app from 'src/store/modules/app'
import * as searchService from 'src/services/search'
import { IAggregatedSearchResult } from 'src/services/search'
import { presenceService } from 'src/services/presence'

export default createSagas<Actions>(actions, {
  login: function* ({ payload }) {
    const login: IResponse<usersAuthService.ILoginData> = yield call(
      usersAuthService.login,
      payload
    )

    sessionService.setSession({
      accessToken: login.data.accessToken,
      expiresIn: login.data.expiresIn,
      tokenType: login.data.tokenType,
      userId: login.data.user.id,
    })

    yield put(
      setters.setSession({
        userId: login.data.user.id,
        accessToken: login.data.accessToken,
        isAfterLogin: true,
      })
    )

    yield put(actions.getProfile.pending())
  },

  recoverPassword: function* ({ payload }) {
    yield call(usersAuthService.recoverPassword, payload)

    yield put(
      app.actions.tost.pending({
        type: 'success',
        title: 'Password recovery',
        description:
          'We have sent an email with a link to reset your password to the address you provided',
      })
    )
  },

  toggleUsersStatus: function* ({ payload }) {
    yield delay(500)

    const users: { data: state.IUser[] } = yield select(
      selectors.allUsersSelector
    )

    const isAllSelectedUsersDeactivated = payload.usersIds.every(
      (id) => !users.data.find((user) => user.id === id)?.isActive
    )

    if (isAllSelectedUsersDeactivated) {
      yield call(usersAuthService.activateUsers, payload)
    } else {
      yield call(usersAuthService.deactivateUsers, payload)
    }

    yield put(
      setters.setUsersStatus({
        state: isAllSelectedUsersDeactivated,
        userIds: payload.usersIds,
      })
    )
  },

  resetPassword: {
    pending: function* ({ payload }) {
      yield call(usersAuthService.resetPassword, payload)

      yield put(
        app.actions.tost.pending({
          type: 'success',
          title: 'Password reset',
          description: 'The password was successfully changed',
        })
      )
    },
    rejected: function* ({ payload }) {
      yield put(
        app.actions.tost.pending({
          type: 'error',
          title: 'Password reset',
          // @ts-ignore
          description: payload?.response?.data?.message,
        })
      )
    },
  },

  getRefreshToken: function* ({ payload }) {
    yield put(setters.setIsUpdatePermissions(payload.isUpdatePermissions))
    const refreshToken: IResponse<usersAuthService.IGetRefreshTokenData> =
      yield call(usersAuthService.getRefreshToken)

    sessionService.setSession({
      accessToken: refreshToken.data.accessToken,
      expiresIn: refreshToken.data.expiresIn,
    })

    yield put(
      setters.setRefreshToken({
        accessToken: refreshToken.data.accessToken,
      })
    )

    const isUpdatePermissions: boolean | undefined = yield select(
      selectors.isUpdatePermissions
    )

    if (isUpdatePermissions) {
      yield put(setters.setIsUpdatePermissions(false))
    }
  },
  getAccounts: function* ({ payload }) {
    const accounts: IResponse<state.IConnectedAccount[]> = yield call(
      accountsService.getAccounts,
      {
        ...payload,
      }
    )

    yield put(setters.setConnectedAccounts(accounts.data))
  },

  logout: function* ({ payload }) {
    // leave presence and close the socket first: teammates (and call routing) see "offline" at
    // once instead of waiting for the connection to time out. Never blocks the logout itself
    try {
      yield call([presenceService, presenceService.stop])
    } catch (e) {
      console.debug(e)
    }

    sessionService.clearSession()
    localStorageService.clearStorage()
    localStorage.removeItem('assistant')
    // yield put(setters.logoutSetter())
    window.location.reload()
  },

  getRoles: function* () {
    const roles: IResponse<state.IRole[]> = yield call(
      usersAuthService.getAllAvailableRoles
    )

    yield put(setters.setRoles(roles.data))
  },

  inviteUsers: function* ({ payload }) {
    yield call(usersAuthService.inviteUsers, payload)
  },

  getAllUsers: {
    effect: 'latest',
    saga: function* ({ payload }) {
      if (payload.search) {
        if (payload.search.length < 2) return

        yield delay(500)

        const searchResponse: searchService.IGetSearchObjectResult<state.IUser>[] =
          yield call(searchService.getSearchObjects<state.IUser>, {
            search: payload.search,
            types: [searchService.SearchType.USER],
            perPage: payload.perPage,
            cursor: payload.cursor,
          })

        const authSection = searchResponse.find(
          (result) => result.service === searchService.ServiceType.AUTH
        )?.sections[0]

        yield put(
          setters.setAllUsers({
            data: authSection?.hits ?? [],
            nextCursor: authSection?.nextCursor ?? null,
            total: authSection?.totalCount ?? authSection?.hits?.length ?? 0,
          })
        )

        return
      }

      const users: IResponse<state.IUser[]> = yield call(
        usersAuthService.getAllUsers,
        payload
      )

      yield put(
        setters.setAllUsers({
          data: users.data,
          nextCursor: users.meta.nextCursor,
          total: users.meta.total,
        })
      )
    },
  },

  loadMoreUsers: {
    effect: 'latest',
    saga: function* ({ payload }) {
      if (payload.search) {
        if (payload.search.length < 2) return

        const searchResponse: searchService.IGetSearchObjectResult<state.IUser>[] =
          yield call(searchService.getSearchObjects<state.IUser>, {
            search: payload.search,
            types: [searchService.SearchType.USER],
            perPage: payload.perPage,
            cursor: payload.cursor,
          })

        const authSection = searchResponse.find(
          (result) => result.service === searchService.ServiceType.AUTH
        )?.sections[0]

        yield put(
          setters.mergeUsers({
            data: authSection?.hits ?? [],
            nextCursor: authSection?.nextCursor ?? null,
          })
        )

        return
      }

      const users: IResponse<state.IUser[]> = yield call(
        usersAuthService.getAllUsers,
        payload
      )

      yield put(setters.mergeUsers(users))
    },
  },

  editUser: function* ({ payload }) {
    const editedUser: IResponse<state.IUser> = yield call(
      usersAuthService.editUserData,
      {
        ...payload,
      }
    )

    yield put(setters.setEditedUser(editedUser.data))
  },

  getProfile: function* ({ payload }) {
    const session = sessionService.getSession()

    if (session.userId) {
      const user: IResponse<usersAuthService.IGetAccountData> = yield call(
        usersAuthService.getAccount
      )

      localStorageService.setStorage({
        user: user.data,
      })

      yield put(setters.setProfile(user.data))
    }
  },

  getOauthClients: function* ({ payload }) {
    const oauthClients: IResponse<any> = yield call(
      oauthService.getOauthClients,
      payload
    )

    // yield put(setters.setOauthClients(oauthClients.data))
  },

  postOauthLogin: function* ({ payload }) {
    yield call(oauthService.postOauthLogib, payload)
  },

  getOauthAuthorize: function* ({ payload }) {
    const result: { codeVerifier?: string } = yield call(
      oauthService.getOauthAuthorize,
      payload
    )

    if (result.codeVerifier) {
      sessionStorage.setItem('oauth_code_verifier', result.codeVerifier)
    }
  },

  postOauthToken: function* ({ payload }) {
    const tokenResponse: IResponse<oauthService.IOauthTokenResponse> =
      yield call(oauthService.postOauthToken, payload)

    if (tokenResponse.data) {
      sessionStorage.removeItem('oauth_code_verifier')

      sessionService.setSession({
        accessToken: tokenResponse.data.access_token,
        expiresIn: tokenResponse.data.expires_in,
        tokenType: tokenResponse.data.token_type as 'bearer',
        userId: tokenResponse.data.user.id,
      })

      yield put(
        setters.setSession({
          userId: tokenResponse.data.user.id,
          accessToken: tokenResponse.data.access_token,
          isAfterLogin: true,
        })
      )

      yield put(actions.getProfile.pending())
    }
  },

  editProfileAvatar: function* ({ payload }) {
    const userAvatar: IResponse<usersAuthService.IGetUserData> = yield call(
      usersAuthService.editAvatarProfile,
      payload
    )

    yield put(setters.setProfileInfo(userAvatar.data?.info))
  },

  editProfile: function* ({ payload }) {
    const user: IResponse<usersAuthService.IGetUserData> = yield call(
      usersAuthService.editProfileData,
      payload
    )

    yield put(setters.setProfileInfo(user.data?.info))
  },

  getBillingStat: function* ({ payload }) {
    const billingStat: IResponse<partnerAuthService.IGetBillingStatData> =
      yield call(partnerAuthService.getBillingStat, payload)

    yield put(setters.setBillingStat(billingStat.data))
  },

  getPartnerCheck: function* () {
    const partnerData: IResponse<IPartner> = yield call(
      partnerAuthService.getPartnerCheck
    )
    yield put(setters.setPartnerData(partnerData.data))
  },
})
